import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowLeft, Mail, Phone, Calendar, Loader2, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentDetails(response.data);
      } catch (err) {
        console.error('Error fetching payment status:', err);
        setError('No pudimos verificar el estado del pago');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#080c2d] via-[#0f133a] to-[#1a1f50] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#aa2aff] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verificando pago...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#080c2d] via-[#0f133a] to-[#1a1f50] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={VIUBUX_LOGO} alt="Viubux" className="h-10 mx-auto" />
        </div>

        {/* Success Card */}
        <div className="bg-[#0f133a]/80 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-10 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              ¡Pago Exitoso!
            </h1>
            <p className="text-green-100 text-lg">
              Tu anticipo ha sido procesado correctamente
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Celebration */}
            <div className="flex items-center justify-center gap-2 mb-6 text-[#aa2aff]">
              <PartyPopper className="w-6 h-6" />
              <span className="text-lg font-medium">¡Gracias por confiar en Viubux!</span>
              <PartyPopper className="w-6 h-6" />
            </div>

            {/* Payment Details */}
            {paymentDetails && paymentDetails.payment_status === 'paid' && (
              <div className="bg-white/5 rounded-xl p-6 mb-6">
                <h3 className="text-white font-semibold mb-4">Detalles del Pago</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monto pagado:</span>
                    <span className="text-green-400 font-bold text-xl">
                      {formatPrice(paymentDetails.amount_total)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Estado:</span>
                    <span className="text-green-400 font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Completado
                    </span>
                  </div>
                  {paymentDetails.metadata?.customer_name && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cliente:</span>
                      <span className="text-white">{paymentDetails.metadata.customer_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-gradient-to-r from-[#aa2aff]/10 to-[#272b96]/10 rounded-xl p-6 mb-6">
              <h3 className="text-white font-semibold mb-4">¿Qué sigue?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#aa2aff] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                  <p className="text-slate-300 text-sm">
                    Recibirás un email de confirmación con los detalles de tu pago
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#aa2aff] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                  <p className="text-slate-300 text-sm">
                    Un miembro de nuestro equipo se pondrá en contacto contigo en las próximas 24-48 horas
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#aa2aff] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                  <p className="text-slate-300 text-sm">
                    Comenzaremos a trabajar en tu proyecto audiovisual
                  </p>
                </li>
              </ul>
            </div>

            {/* Contact Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <a 
                href="https://wa.me/525548564696" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors text-slate-300 hover:text-green-400"
              >
                <Phone className="w-5 h-5" />
                <span className="text-sm">WhatsApp</span>
              </a>
              <a 
                href="mailto:email@viubux.com"
                className="flex items-center justify-center gap-2 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors text-slate-300 hover:text-[#aa2aff]"
              >
                <Mail className="w-5 h-5" />
                <span className="text-sm">Email</span>
              </a>
              <a 
                href="https://vbx.viubux.com/agenda" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors text-slate-300 hover:text-[#aa2aff]"
              >
                <Calendar className="w-5 h-5" />
                <span className="text-sm">Agendar</span>
              </a>
            </div>

            {/* Back Button */}
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-[#aa2aff] to-[#7012b2] hover:from-[#7012b2] hover:to-[#aa2aff] text-white py-6"
              data-testid="btn-back-home"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Volver al Cotizador
            </Button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-8">
          © {new Date().getFullYear()} Viubux. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}

export default PaymentSuccess;
